import type { WebContainer } from '@webcontainer/api';
import { workbenchStore } from '../stores/workbench';
import { persistUserFile } from '../runtime/tool-adapters/writeFile';
import { bytesToBase64, registerAssetInStore } from '../runtime/tool-adapters/writeAsset';
import { executePrune } from '../runtime/tool-adapters/pruneWorkspace';
import { ensureHotEditBridgeFiles } from './ensureHotEditBridge';
import { normalizeWorkbenchPath } from './normalizeWorkbenchPath';
import { parseSnapshotZipBlob } from './parseSnapshotZipBlob';
import { wcPerfAsync } from './workbenchPerf';

/** Extensions we treat as binary; .svg stays UTF-8 text. */
const BINARY_EXT =
  /\.(png|jpe?g|gif|webp|ico|woff2?|ttf|eot|pdf|mp4|webm|mp3|wav|zip|wasm|avif)$/i;

/** Parallel writes reduce wall time for large restores. */
const SNAPSHOT_WRITE_CONCURRENCY = 14;

async function runPool<T>(items: T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  if (items.length === 0) return;
  let i = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const idx = i++;
      if (idx >= items.length) break;
      await fn(items[idx]);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker()),
  );
}

function normalizeZipPath(path: string): string | null {
  let p = path.replace(/^\.\/+/, '').replace(/\\/g, '/');
  if (!p || p.endsWith('/')) return null;
  if (p.startsWith('__MACOSX/') || p.includes('/__MACOSX/')) return null;
  if (p === '.DS_Store' || p.endsWith('/.DS_Store')) return null;
  return normalizeWorkbenchPath(p);
}

/**
 * Apply a snapshot zip (same shape as buildPrototypeSnapshotZipBlob) onto WebContainer + workbench store.
 * Replaces prior user files/assets — does not merge.
 */
export async function applySnapshotZipToWebContainer(
  wc: WebContainer,
  blob: Blob,
  signal?: AbortSignal,
): Promise<void> {
  return wcPerfAsync('wc:snapshot:applyZip', async () => {
    workbenchStore.restoreInProgress.set(true);
    try {
      if (signal?.aborted) {
        throw new DOMException('Snapshot restore aborted', 'AbortError');
      }

      const zipEntries = await parseSnapshotZipBlob(blob);
      type EntryPlan = {
        normalized: string;
        u8: Uint8Array;
        isBinary: boolean;
      };

      const plans: EntryPlan[] = [];
      for (const { path, u8 } of zipEntries) {
        if (signal?.aborted) {
          throw new DOMException('Snapshot restore aborted', 'AbortError');
        }
        const normalized = normalizeZipPath(path);
        if (!normalized) continue;
        plans.push({ normalized, u8, isBinary: BINARY_EXT.test(normalized) });
      }

      if (signal?.aborted) {
        throw new DOMException('Snapshot restore aborted', 'AbortError');
      }

      workbenchStore.files.set({});
      workbenchStore.assets.set({});
      workbenchStore.lastToolWrittenByPath.set({});
      workbenchStore.fileConflicts.set({});
      // Restoring wholesale replaces any pending manual edits with the (already-built)
      // snapshot content, so there's nothing left "unbuilt" relative to what's now loaded.
      workbenchStore.hasUnbuiltEdits.set(false);

      await runPool(plans, SNAPSHOT_WRITE_CONCURRENCY, async ({ normalized, u8, isBinary }) => {
        if (signal?.aborted) return;
        if (isBinary) {
          const dir = normalized.includes('/')
            ? normalized.substring(0, normalized.lastIndexOf('/'))
            : '';
          if (dir) {
            await wc.fs.mkdir(dir, { recursive: true });
          }
          await wc.fs.writeFile(normalized, u8);
          registerAssetInStore(normalized, bytesToBase64(u8));
          return;
        }

        const content = new TextDecoder('utf-8', { fatal: false }).decode(u8);
        await persistUserFile(wc, normalized, content, false);
      });

      if (signal?.aborted) {
        throw new DOMException('Snapshot restore aborted', 'AbortError');
      }

      // The writes above only ever add/overwrite paths present in this snapshot — a file
      // that existed in the WebContainer from a later/different generation (e.g. restoring
      // an earlier turn after a later one added a page) is never removed, so it stays on
      // disk and can affect the next build's type-check even though the app's own file
      // list no longer references it. `executePrune` removes stale generated files under
      // `src/` while preserving template infra, the hot-edit bridge, and this snapshot's
      // own paths.
      await executePrune(
        wc,
        plans.map((p) => p.normalized),
      );

      if (signal?.aborted) {
        throw new DOMException('Snapshot restore aborted', 'AbortError');
      }

      await ensureHotEditBridgeFiles(wc);
    } finally {
      window.setTimeout(() => {
        workbenchStore.restoreInProgress.set(false);
      }, 4_500);
    }
  });
}
